/* Farpoint Options — storefront controller
 *
 * Responsibilities (display + convenience only):
 *   - conditional show/hide of options
 *   - running "options total" preview
 *   - friendly inline validation before submit
 *   - writing form inputs so the selection travels with the cart
 *
 * What it deliberately does NOT do: decide prices, resolve variants, add
 * anything to the cart, or be the authority on validity. It writes only
 * human-readable labels plus a compact key-only `_fp_config`. Shopify Functions
 * resolve pricing and enforce validity server-side, so a bypassed or tampered
 * client cannot produce a wrong order.
 *
 * 2026-07-31: this replaced a STALE copy in the theme (~6.9 KB) that predated
 * the Cart Transform and added each selected add-on as its own cart line via a
 * second /cart/add.js call, tagged `_fp_addon_for` / `_fp_option`. With the
 * Cart Transform also bundling the add-on, every add-on was charged twice.
 * Never reintroduce a cart write here. The browser sends keys; Functions price.
 */
(function () {
  "use strict";

  /* Bumped whenever this file changes. The theme keeps its OWN copy of this
     asset and nothing keeps it in sync with the repo, so a stale copy has
     already caused a live double-charge once. Check this in the console
     against the repo before debugging anything else. */
  window.FP_OPTIONS_VERSION = "2026-07-31.2";

  var roots = document.querySelectorAll("[data-fp-options]");
  for (var i = 0; i < roots.length; i++) init(roots[i]);

  function init(root) {
    var specEl = document.querySelector("[data-fp-spec]");
    if (!specEl) return;

    var spec;
    try {
      spec = JSON.parse(specEl.textContent);
      if (typeof spec === "string") spec = JSON.parse(spec);
    } catch (e) {
      return; // fail quiet; server still validates
    }
    if (!spec || !Array.isArray(spec.options)) return;

    var form = root.closest("form[action*='/cart/add']") ||
               document.querySelector("form[action*='/cart/add']");

    var optionEls = root.querySelectorAll("[data-fp-option]");

    root.addEventListener("change", onInteract);
    root.addEventListener("input", onInteract);
    root.addEventListener("click", function (e) {
      var sw = e.target.closest(".fp-option__swatch");
      if (!sw) return;
      e.preventDefault();
      var group = sw.parentNode;
      var all = group.querySelectorAll(".fp-option__swatch");
      for (var i = 0; i < all.length; i++) {
        all[i].classList.remove("is-selected");
        all[i].setAttribute("aria-pressed", "false");
      }
      sw.classList.add("is-selected");
      sw.setAttribute("aria-pressed", "true");
      onInteract();
    });

    if (form) {
      form.addEventListener("submit", function (e) {
        sync();
        if (!validate()) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    onInteract();

    function onInteract() {
      applyConditions();
      sync();
      renderTotal();
    }

    /* ---------- selections ---------- */

    function readSelections() {
      var out = {};
      for (var i = 0; i < optionEls.length; i++) {
        var el = optionEls[i];
        if (el.hidden) continue;
        var key = el.getAttribute("data-fp-option");
        var type = el.getAttribute("data-fp-type");
        var vals = [];

        if (type === "dropdown") {
          var sel = el.querySelector("select");
          if (sel && sel.value) vals.push(sel.value);
        } else if (type === "radio") {
          var r = el.querySelector("input[type=radio]:checked");
          if (r) vals.push(r.value);
        } else if (type === "checkbox" || type === "checkbox_group") {
          var boxes = el.querySelectorAll("input[type=checkbox]:checked");
          for (var b = 0; b < boxes.length; b++) vals.push(boxes[b].value);
        } else if (type === "swatch") {
          var s = el.querySelector(".fp-option__swatch.is-selected");
          if (s) vals.push(s.getAttribute("data-fp-value"));
        } else {
          var t = el.querySelector("input, textarea");
          if (t && String(t.value).trim() !== "") vals.push(String(t.value).trim());
        }

        if (vals.length) out[key] = vals;
      }
      return out;
    }

    /* ---------- conditional logic ---------- */

    function applyConditions() {
      var chosen = readSelections();
      for (var i = 0; i < optionEls.length; i++) {
        var el = optionEls[i];
        var raw = el.getAttribute("data-fp-conditions");
        if (!raw) continue;
        var rules;
        try { rules = JSON.parse(raw); } catch (e) { continue; }
        if (!Array.isArray(rules) || !rules.length) continue;
        el.hidden = !visible(rules, el.getAttribute("data-fp-match") || "all", chosen);
      }
    }

    function visible(rules, match, chosen) {
      var all = match === "all";
      var hides = [], shows = [];
      for (var i = 0; i < rules.length; i++) {
        (rules[i].action === "hide" ? hides : shows).push(rules[i]);
      }
      if (hides.length) {
        var h = all ? hides.every(function (r) { return evalRule(r, chosen); })
                    : hides.some(function (r) { return evalRule(r, chosen); });
        if (h) return false;
      }
      if (!shows.length) return true;
      return all ? shows.every(function (r) { return evalRule(r, chosen); })
                 : shows.some(function (r) { return evalRule(r, chosen); });
    }

    function evalRule(rule, chosen) {
      var actual = chosen[rule.when] || [];
      switch (rule.op) {
        case "is":           return actual.length === 1 && actual[0] === rule.value;
        case "is_not":       return actual.indexOf(rule.value) === -1;
        case "contains":     return actual.indexOf(rule.value) !== -1;
        case "not_contains": return actual.indexOf(rule.value) === -1;
        case "is_empty":     return actual.length === 0;
        case "is_not_empty": return actual.length > 0;
        default:             return true;
      }
    }

    /* ---------- form sync ---------- */

    function sync() {
      if (!form) return;
      var old = form.querySelectorAll("[data-fp-injected]");
      for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);

      var chosen = readSelections();
      var pairs = [];

      for (var o = 0; o < spec.options.length; o++) {
        var opt = spec.options[o];
        var el = root.querySelector('[data-fp-option="' + cssEscape(opt.key) + '"]');
        if (el && el.hidden) continue;

        var picked = chosen[opt.key];
        if (!picked || !picked.length) continue;

        var labels = [];
        for (var p = 0; p < picked.length; p++) {
          var v = findValue(opt, picked[p]);
          var text = v ? v.label : picked[p];
          // Show what each paid add-on costs, so the customer can see the
          // package price rather than one opaque bundle total. Descriptive
          // only — the Cart Transform remains the authority on what is charged.
          var amount = v ? Number(v.price || 0) : 0;
          if (amount > 0) text += " — " + format(amount);
          labels.push(text);
          pairs.push([opt.key, v ? v.key : picked[p]]);
        }
        // Human-readable, SELECTED ONLY — no blank rows, no internal codes.
        inject("properties[" + opt.label + "]", labels.join(", "));
      }

      if (pairs.length) {
        inject("properties[_fp_config]", JSON.stringify({
          v: 1,
          rev: Number(root.getAttribute("data-fp-rev") || 1),
          sel: pairs
        }));
      }
    }

    function inject(name, value) {
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      input.setAttribute("data-fp-injected", "");
      form.appendChild(input);
    }

    /* ---------- validation + total (UX only) ---------- */

    function validate() {
      var ok = true, firstBad = null;
      var chosen = readSelections();
      for (var i = 0; i < optionEls.length; i++) {
        var el = optionEls[i];
        var errEl = el.querySelector("[data-fp-error]");
        if (errEl) { errEl.hidden = true; errEl.textContent = ""; }
        if (el.hidden) continue;
        if (el.getAttribute("data-fp-required") !== "true") continue;
        var key = el.getAttribute("data-fp-option");
        if (!chosen[key] || !chosen[key].length) {
          ok = false;
          if (errEl) {
            errEl.textContent = "Please choose " + el.getAttribute("data-fp-label") + ".";
            errEl.hidden = false;
          }
          if (!firstBad) firstBad = el;
        }
      }
      if (firstBad && firstBad.scrollIntoView) {
        firstBad.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return ok;
    }

    function renderTotal() {
      var box = root.querySelector("[data-fp-summary]");
      var out = root.querySelector("[data-fp-total]");
      if (!box || !out) return;
      var chosen = readSelections(), total = 0;
      for (var o = 0; o < spec.options.length; o++) {
        var opt = spec.options[o];
        var el = root.querySelector('[data-fp-option="' + cssEscape(opt.key) + '"]');
        if (el && el.hidden) continue;
        var picked = chosen[opt.key] || [];
        for (var p = 0; p < picked.length; p++) {
          var v = findValue(opt, picked[p]);
          if (v && v.price) total += Number(v.price) || 0;
        }
      }
      if (total > 0) {
        out.textContent = format(total);
        box.hidden = false;
      } else {
        box.hidden = true;
      }
    }

    function findValue(opt, key) {
      if (!Array.isArray(opt.values)) return null;
      for (var i = 0; i < opt.values.length; i++) {
        if (opt.values[i].key === key) return opt.values[i];
      }
      return null;
    }

    function format(n) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US",
          { style: "currency", currency: "USD" }).format(n);
      } catch (e) {
        return "$" + n.toFixed(2);
      }
    }

    function cssEscape(s) {
      return String(s).replace(/["\\]/g, "\\$&");
    }
  }
})();
